import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  DatePicker,
  Row,
  Col,
  Tag,
  Typography,
  Empty,
  Statistic,
  Flex,
  Alert,
  InputNumber,
  Select,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import {
  SearchOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  RiseOutlined,
  FallOutlined,
  BankOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  useGetAllFinanceTreeTransactionLinesQuery,
  useGetAllFinanceTreeQuery,
} from "../graphql/generated/hooks";
import type { GetAllFinanceTreeTransactionLinesQuery } from "../graphql/generated/operations";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Type for transaction line from GraphQL
type TransactionLine = GetAllFinanceTreeTransactionLinesQuery['getAllFinanceTreeTransactionLines']['transactionLines'][0];

function FinanceTreeTransactionLines() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get financeTreeId from URL params
  const urlFinanceTreeId = searchParams.get("financeTreeId");
  // State - UI filters (not applied yet)
  const [selectedFinanceTreeId, setSelectedFinanceTreeId] = useState<number | null>(
    urlFinanceTreeId ? parseInt(urlFinanceTreeId) : null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [minDebit, setMinDebit] = useState<number | null>(null);
  const [maxDebit, setMaxDebit] = useState<number | null>(null);
  const [minCredit, setMinCredit] = useState<number | null>(null);
  const [maxCredit, setMaxCredit] = useState<number | null>(null);
  const [transactionId, setTransactionId] = useState<number | null>(null);

  // Applied filters (actually used in query)
  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: "",
    dateRange: [null, null] as [Dayjs | null, Dayjs | null],
    minDebit: null as number | null,
    maxDebit: null as number | null,
    minCredit: null as number | null,
    maxCredit: null as number | null,
    transactionId: null as number | null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch all finance tree items for the dropdown
  const { data: financeTreeData, loading: loadingFinanceTree } = useGetAllFinanceTreeQuery({
    fetchPolicy: "cache-first",
  });

  // Get all finance tree items
  const financeTreeItems = financeTreeData?.getAllFinanceTreeItemsWithLevel || [];

  // Find the selected finance tree item
  const selectedFinanceTreeItem = financeTreeItems.find(
    (item) => item.id === selectedFinanceTreeId
  );

  // Build filter object - only include non-null values
  const filter = {
    ...(selectedFinanceTreeId && { financeTreeId: selectedFinanceTreeId }),
    ...(appliedFilters.dateRange[0] && { dateFrom: appliedFilters.dateRange[0].format("YYYY-MM-DD") }),
    ...(appliedFilters.dateRange[1] && { dateTo: appliedFilters.dateRange[1].format("YYYY-MM-DD") }),
    ...(appliedFilters.searchTerm && { description: appliedFilters.searchTerm }),
    ...(appliedFilters.minDebit !== null && { minDebit: appliedFilters.minDebit }),
    ...(appliedFilters.maxDebit !== null && { maxDebit: appliedFilters.maxDebit }),
    ...(appliedFilters.minCredit !== null && { minCredit: appliedFilters.minCredit }),
    ...(appliedFilters.maxCredit !== null && { maxCredit: appliedFilters.maxCredit }),
    ...(appliedFilters.transactionId !== null && { transactionId: appliedFilters.transactionId }),
  };

  // Only pass filter if it has at least one property
  const hasFilters = Object.keys(filter).length > 0;

  // Fetch transaction lines using GraphQL
  const { data, loading, error } = useGetAllFinanceTreeTransactionLinesQuery({
    variables: {
      limit: pageSize,
      page: currentPage,
      filter: hasFilters ? filter : {},
    },
    skip: !selectedFinanceTreeId,
  });

  const transactionLines = data?.getAllFinanceTreeTransactionLines?.transactionLines || [];
  const total = data?.getAllFinanceTreeTransactionLines?.total || 0;
  // Calculate totals
  const totals = transactionLines.reduce(
    (acc, line) => ({
      totalDebit: acc.totalDebit + line.debit,
      totalCredit: acc.totalCredit + line.credit,
    }),
    { totalDebit: 0, totalCredit: 0 }
  );

  // Handle pagination change
  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setAppliedFilters({
      searchTerm,
      dateRange,
      minDebit,
      maxDebit,
      minCredit,
      maxCredit,
      transactionId,
    });
    setCurrentPage(1);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setSearchTerm("");
    setDateRange([null, null]);
    setMinDebit(null);
    setMaxDebit(null);
    setMinCredit(null);
    setMaxCredit(null);
    setTransactionId(null);
    setAppliedFilters({
      searchTerm: "",
      dateRange: [null, null],
      minDebit: null,
      maxDebit: null,
      minCredit: null,
      maxCredit: null,
      transactionId: null,
    });
    setCurrentPage(1);
  };

  // Table columns
  const columns: ColumnsType<TransactionLine> = [
    {
      title: <Text strong>{t("label.transaction.order_id")}</Text>,
      dataIndex: "transactionId",
      key: "transactionId",
      width: 120,
      render: (transactionId: number) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          #{transactionId}
        </Tag>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.description")}</Text>,
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description: string | null) => (
        <Space size="small">
          <FileTextOutlined style={{ color: "#1890ff" }} />
          <Text>{description || "-"}</Text>
        </Space>
      ),
    },
    {
      title: <Text strong>{t("label.finance_tree_item")}</Text>,
      dataIndex: "financeTree",
      key: "financeTree",
      ellipsis: true,
      render: (_: TransactionLine["financeTree"], record) => {
        const financeTreeName = record.financeTree
          ? currentLang === "ar"
            ? record.financeTree.nameAr
            : record.financeTree.name
          : null;
        return (
          <Text strong style={{ color: financeTreeName ? "#1d1d1f" : "#8c8c8c" }}>
            {financeTreeName || "-"}
          </Text>
        );
      },
    },
    {
      title: <Text strong>{t("label.total_debit")}</Text>,
      dataIndex: "debit",
      key: "debit",
      width: 150,
      align: "right",
      render: (amount: number) => (
        <Space>
          {amount > 0 && <FallOutlined style={{ color: "#cf1322" }} />}
          <Text strong style={{ color: amount > 0 ? "#cf1322" : "#8c8c8c" }}>
            {amount.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Space>
      ),
    },
    {
      title: <Text strong>{t("label.total_credit")}</Text>,
      dataIndex: "credit",
      key: "credit",
      width: 150,
      align: "right",
      render: (amount: number) => (
        <Space>
          {amount > 0 && <RiseOutlined style={{ color: "#3f8600" }} />}
          <Text strong style={{ color: amount > 0 ? "#3f8600" : "#8c8c8c" }}>
            {amount.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Space>
      ),
    },
    {
      title: <Text strong>{t("label.created_at")}</Text>,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (date: string) => (
        <Text type="secondary">
          {dayjs(date).format("DD/MM/YYYY HH:mm")}
        </Text>
      ),
    },
    {
      title: <Text strong>{t("label.actions")}</Text>,
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/finance/transactions/view/${record.transactionId}`)}
          >
            {t("button.view")}
          </Button>
        </Space>
      ),
    },
  ];

  // Get display name for selected item
  const getFinanceTreeDisplayName = (item: any) => {
    return currentLang === "ar" ? item.nameAr : item.name;
  };

  // Show error
  if (error) {
    return (
      <RobinPage title={t("label.transaction_lines")}>
        <Alert
          message={t("error.failed_to_load")}
          description={error.message}
          type="error"
          showIcon
          style={{ margin: "20px 0" }}
        />
      </RobinPage>
    );
  }

  return (
    <RobinPage
      title={t("label.transaction_lines")}
      actions={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/finance/tree")}
          size="large"
        >
          {t("button.back_to_tree")}
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Show message if no finance tree is selected */}
        {!selectedFinanceTreeId && (
          <Alert
            message={t("label.no_finance_tree_selected")}
            description={t("label.please_select_finance_tree")}
            type="info"
            showIcon
            icon={<BankOutlined />}
          />
        )}

        {/* Statistics */}
        {selectedFinanceTreeId && (
          <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title={t("label.total_debit")}
                value={totals.totalDebit}
                precision={2}
                valueStyle={{ color: "#cf1322" }}
                prefix={<FallOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title={t("label.total_credit")}
                value={totals.totalCredit}
                precision={2}
                valueStyle={{ color: "#3f8600" }}
                prefix={<RiseOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title={t("label.balance")}
                value={totals.totalDebit - totals.totalCredit}
                precision={2}
                valueStyle={{
                  color: totals.totalDebit - totals.totalCredit >= 0 ? "#3f8600" : "#cf1322",
                }}
              />
            </Card>
          </Col>
        </Row>
        )}

        {/* Filters Card */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            background: "linear-gradient(to bottom, #fafafa 0%, #ffffff 100%)",
          }}
          styles={{
            body: { padding: "28px 32px" },
          }}
        >
          <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
            <Space size="middle">
              <SearchOutlined style={{ fontSize: 20, color: "#667eea" }} />
              <Text strong style={{ fontSize: 16, color: "#262626" }}>
                {t("label.filters")}
              </Text>
            </Space>
            <Space size="middle">
              <Button
                size="large"
                icon={<ReloadOutlined />}
                style={{
                  borderRadius: 8,
                  height: 40,
                  paddingLeft: 20,
                  paddingRight: 20,
                }}
                onClick={handleResetFilters}
                disabled={!selectedFinanceTreeId}
              >
                {t("button.reset_filters")}
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                style={{
                  borderRadius: 8,
                  height: 40,
                  paddingLeft: 20,
                  paddingRight: 20,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
                onClick={handleApplyFilters}
                disabled={!selectedFinanceTreeId}
              >
                {t("button.search")}
              </Button>
            </Space>
          </Flex>
          <Row gutter={[16, 20]}>
            {/* First Row */}
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.finance_tree_item")}
                </Text>
                <Select
                  showSearch
                  allowClear
                  size="large"
                  placeholder={t("placeholder.select_finance_tree_item")}
                  value={selectedFinanceTreeId ?? undefined}
                  onChange={(value) => {
                    const newValue = typeof value === "number" ? value : null;
                    setSelectedFinanceTreeId(newValue);
                    setCurrentPage(1);
                  }}
                  loading={loadingFinanceTree}
                  style={{ width: "100%" }}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={financeTreeItems.map((item) => ({
                    value: item.id,
                    label: `${item.serialNumber} - ${getFinanceTreeDisplayName(item)}`,
                  }))}
                  suffixIcon={<BankOutlined />}
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.finance_transaction.description")}
                </Text>
                <Input
                  placeholder={t("placeholder.search_description")}
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onPressEnter={handleApplyFilters}
                  allowClear
                  size="large"
                  style={{ borderRadius: 8 }}
                  disabled={!selectedFinanceTreeId}
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.transaction.order_id")}
                </Text>
                <InputNumber
                  placeholder={t("placeholder.transaction_id")}
                  value={transactionId}
                  onChange={(value) => setTransactionId(value)}
                  onPressEnter={handleApplyFilters}
                  style={{ width: "100%", borderRadius: 8 }}
                  size="large"
                  disabled={!selectedFinanceTreeId}
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.date_range")}
                </Text>
                <RangePicker
                  style={{
                    width: "100%",
                    borderRadius: 8,
                  }}
                  format="DD/MM/YYYY"
                  placeholder={[
                    t("placeholder.start_date"),
                    t("placeholder.end_date"),
                  ]}
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
                  size="large"
                  disabled={!selectedFinanceTreeId}
                />
              </Space>
            </Col>
            {/* Second Row */}
            <Col xs={12} sm={6} md={3}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.min_debit")}
                </Text>
                <InputNumber
                  placeholder="0.00"
                  value={minDebit}
                  onChange={(value) => setMinDebit(value)}
                  onPressEnter={handleApplyFilters}
                  style={{ width: "100%", borderRadius: 8 }}
                  size="large"
                  disabled={!selectedFinanceTreeId}
                  prefix="﷼"
                />
              </Space>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.max_debit")}
                </Text>
                <InputNumber
                  placeholder="0.00"
                  value={maxDebit}
                  onChange={(value) => setMaxDebit(value)}
                  onPressEnter={handleApplyFilters}
                  style={{ width: "100%", borderRadius: 8 }}
                  size="large"
                  disabled={!selectedFinanceTreeId}
                  prefix="﷼"
                />
              </Space>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.min_credit")}
                </Text>
                <InputNumber
                  placeholder="0.00"
                  value={minCredit}
                  onChange={(value) => setMinCredit(value)}
                  onPressEnter={handleApplyFilters}
                  style={{ width: "100%", borderRadius: 8 }}
                  size="large"
                  disabled={!selectedFinanceTreeId}
                  prefix="﷼"
                />
              </Space>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 13, color: "#595959" }}>
                  {t("label.max_credit")}
                </Text>
                <InputNumber
                  placeholder="0.00"
                  value={maxCredit}
                  onChange={(value) => setMaxCredit(value)}
                  onPressEnter={handleApplyFilters}
                  style={{ width: "100%", borderRadius: 8 }}
                  size="large"
                  disabled={!selectedFinanceTreeId}
                  prefix="﷼"
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        {selectedFinanceTreeId && (
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          title={
            <Space direction="vertical" size={0}>
              <Text strong style={{ fontSize: 16 }}>
                {t("label.transaction_lines")}
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("label.showing")} {transactionLines.length} {t("label.of")} {total}{" "}
                {t("label.transaction_lines")}
              </Text>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={transactionLines}
            loading={loading}
            rowKey="id"
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("label.of")} ${total} ${t("label.transaction_lines")}`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    hasFilters
                      ? t("label.no_search_results")
                      : t("label.no_transaction_lines")
                  }
                />
              ),
            }}
            bordered
            scroll={{ x: 1200 }}
          />
        </Card>
        )}
      </Space>
    </RobinPage>
  );
}

export default FinanceTreeTransactionLines;

